/**
 * debugSessionController — the client-side state machine for a free-roam Debug
 * Session. A module-level singleton (like debugService) rather than a React
 * context, so it needs no provider mount (App.tsx is frozen) and survives
 * navigation. The DevModeIndicator renders the overlay bound to this store.
 *
 * Live-save: every finding is POSTed to the server the moment it's saved, and
 * the active session token is mirrored to localStorage — so a hard refresh
 * loses nothing and the session can be resumed (resumeIfAny) on next load.
 */
import { useSyncExternalStore } from 'react';
import { debugService } from './debugService';
import { debugSessionApi, type DebugFinding, type FindingPayload } from './debugSessionApi';

const ACTIVE_KEY = 'bsg_debug_active_session';

export interface DebugSessionState {
  active: boolean;
  token: string | null;
  title: string | null;
  findings: DebugFinding[];
  busy: boolean;
  capturing: boolean;
  error: string | null;
}

class DebugSessionController {
  private state: DebugSessionState = {
    active: false, token: null, title: null, findings: [], busy: false, capturing: false, error: null,
  };
  private listeners = new Set<() => void>();

  subscribe = (fn: () => void): (() => void) => {
    this.listeners.add(fn);
    return () => { this.listeners.delete(fn); };
  };
  getState = (): Readonly<DebugSessionState> => this.state;
  private emit() { this.listeners.forEach((l) => l()); }
  private set(patch: Partial<DebugSessionState>) { this.state = { ...this.state, ...patch }; this.emit(); }

  /** Front-end repro/env context attached to the session (role/tenant are added
   *  by the overlay, which has auth). */
  buildRepro(extra?: Record<string, unknown>): Record<string, unknown> {
    const dv = debugService.getDeployVersion();
    return {
      url: window.location.href,
      route: window.location.pathname,
      viewport: `${window.innerWidth}x${window.innerHeight}`,
      devicePixelRatio: window.devicePixelRatio,
      theme: document.documentElement.getAttribute('data-theme')
        || (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'),
      browser: navigator.userAgent,
      deploy_sha: dv?.commit_sha ?? null,
      deploy_sha_short: dv?.commit_sha_short ?? null,
      environment: dv?.environment ?? (window.location.hostname.includes('staging') ? 'staging' : 'production'),
      ...extra,
    };
  }

  async start(title: string, extraRepro?: Record<string, unknown>): Promise<void> {
    if (this.state.active) return;
    this.set({ busy: true, error: null });
    try {
      // deploy_sha raced to null on the first prod session: buildRepro read
      // getDeployVersion() before the /api/version fetch landed. Wait for it
      // (2s cap — a hung fetch must not block starting a session).
      if (!debugService.getDeployVersion()) {
        await Promise.race([
          debugService.fetchDeployVersion(),
          new Promise((r) => setTimeout(r, 2000)),
        ]);
      }
      const repro = this.buildRepro(extraRepro);
      const { session_token } = await debugSessionApi.create({
        title,
        environment: String(repro.environment || 'production'),
        deploy_sha: debugService.getDeployVersion()?.commit_sha,
        repro,
      });
      localStorage.setItem(ACTIVE_KEY, JSON.stringify({ token: session_token, title }));
      this.set({ active: true, token: session_token, title, findings: [], busy: false });
    } catch (e: any) {
      this.set({ busy: false, error: e?.message || 'Failed to start session' });
    }
  }

  /** On app load, restore an in-progress session from the localStorage pointer. */
  async resumeIfAny(): Promise<void> {
    if (this.state.active) return;
    let saved: { token: string; title?: string } | null = null;
    try { const raw = localStorage.getItem(ACTIVE_KEY); if (raw) saved = JSON.parse(raw); } catch { /* ignore */ }
    if (!saved?.token) return;
    try {
      const detail = await debugSessionApi.get(saved.token);
      if (detail.status === 'in_progress') {
        this.set({ active: true, token: detail.session_token, title: detail.title, findings: detail.findings });
      } else {
        localStorage.removeItem(ACTIVE_KEY);
      }
    } catch {
      localStorage.removeItem(ACTIVE_KEY); // stale/deleted pointer
    }
  }

  async addFinding(payload: FindingPayload, shots?: { screenshot?: Blob | null; annotated?: Blob | null; extra?: Blob[] }): Promise<DebugFinding | null> {
    if (!this.state.token) return null;
    this.set({ busy: true, error: null });
    try {
      // Attach the recent network (HAR) + console around the capture so a
      // finding carries the API calls / errors that explain it. Empty → omit.
      const net = payload.network ?? debugService.getRecentNetworkHar();
      const con = payload.console ?? debugService.getRecentConsole();
      // F2 — the "steps before capture" trail rides the finding's repro block
      // (repro_json column; no schema change).
      const trail = debugService.getRecentTrail();
      const finding = await debugSessionApi.addFinding(
        this.state.token,
        {
          ...payload,
          route: payload.route ?? window.location.pathname,
          network: (net as any)?.entries?.length ? net : undefined,
          console: (con as any)?.errors?.length ? con : undefined,
          repro: trail.length ? { ...((payload.repro as Record<string, unknown>) || {}), trail } : payload.repro,
        },
        shots,
      );
      this.set({ findings: [...this.state.findings, finding], busy: false });
      // If the finding carried failed API calls, auto-attach a backend-state
      // snapshot (server logs + deploy sha) so front + back end are correlated.
      const token = this.state.token;
      const failed = (((net as any)?.entries as any[]) || []).filter((e) => (e?.response?.status || 0) >= 400);
      if (token && finding && failed.length) {
        void debugSessionApi.backendSnapshot(token, finding.seq, {
          failed_requests: failed.map((e) => ({ method: e?.request?.method, url: e?.request?.url, status: e?.response?.status })),
        }).then((updated) => {
          this.set({ findings: this.state.findings.map((f) => (f.seq === updated.seq ? updated : f)) });
        }).catch(() => { /* non-fatal */ });
      }
      return finding;
    } catch (e: any) {
      this.set({ busy: false, error: e?.message || 'Failed to save finding' });
      return null;
    }
  }

  async updateFinding(seq: number, patch: Partial<FindingPayload> & { linked_ticket_id?: number | null }): Promise<void> {
    if (!this.state.token) return;
    try {
      const updated = await debugSessionApi.updateFinding(this.state.token, seq, patch);
      this.set({ findings: this.state.findings.map((f) => (f.seq === seq ? updated : f)) });
    } catch (e: any) {
      this.set({ error: e?.message || 'Failed to update finding' });
    }
  }

  async deleteFinding(seq: number): Promise<void> {
    if (!this.state.token) return;
    try {
      await debugSessionApi.deleteFinding(this.state.token, seq);
      this.set({ findings: this.state.findings.filter((f) => f.seq !== seq) });
    } catch (e: any) {
      this.set({ error: e?.message || 'Failed to delete finding' });
    }
  }

  /** Hide the overlay while a true-pixel/full-page grab is in flight. */
  setCapturing(v: boolean): void { this.set({ capturing: v }); }

  async finish(): Promise<string | null> {
    const token = this.state.token;
    if (!token) return null;
    this.set({ busy: true });
    try { await debugSessionApi.finalize(token); } catch { /* keep the artifacts regardless */ }
    debugService.stopDisplayStream();
    localStorage.removeItem(ACTIVE_KEY);
    this.set({ active: false, token: null, title: null, findings: [], busy: false });
    return token;
  }

  /** Close the overlay but leave the (in-progress) session on the server AND
   *  keep the localStorage pointer, so a reload auto-resumes it and the DEV
   *  banner ⏺ can offer to resume — closing the HUD never strands a session. */
  cancel(): void {
    debugService.stopDisplayStream();
    this.set({ active: false, token: null, title: null, findings: [], busy: false, capturing: false });
  }

  /** The locally-pointed resumable session, if any (validity is checked by
   *  resumeIfAny — this only reads the pointer for UI affordances). */
  resumablePointer(): { token: string; title?: string } | null {
    try {
      const raw = localStorage.getItem(ACTIVE_KEY);
      const saved = raw ? JSON.parse(raw) : null;
      return saved?.token ? saved : null;
    } catch { return null; }
  }
}

export const debugSessionController = new DebugSessionController();

export function useDebugSession(): Readonly<DebugSessionState> {
  return useSyncExternalStore(debugSessionController.subscribe, debugSessionController.getState);
}

export default debugSessionController;
